import { cn } from "@/lib/utils";
import { Info, X } from "lucide-react";
import { useId, useState } from "react";
import { createPortal } from "react-dom";

/** Info button that opens a readable explanation popup (not hover-only). */
export function InfoExplain({
	label,
	title,
	children,
	className,
}: {
	label: string;
	title: string;
	children: React.ReactNode;
	className?: string;
}) {
	const id = useId();
	const [open, setOpen] = useState(false);

	return (
		<span className={cn("relative inline-flex items-center gap-1", className)}>
			<span>{label}</span>
			<button
				type="button"
				className="inline-flex size-4 items-center justify-center rounded-full border border-border/80 text-muted-foreground hover:border-primary/50 hover:text-primary"
				aria-label={`${title} — more info`}
				onClick={(e) => {
					e.stopPropagation();
					setOpen(true);
				}}
			>
				<Info className="size-2.5" aria-hidden />
			</button>
			{open && typeof document !== "undefined"
				? createPortal(
						<div
							className="fixed inset-0 z-[120] flex items-center justify-center p-4"
							onClick={() => setOpen(false)}
						>
							<div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
							<div
								role="dialog"
								aria-modal
								aria-labelledby={id}
								className="relative w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-card"
								onClick={(e) => e.stopPropagation()}
							>
								<div className="flex items-start justify-between gap-3">
									<div>
										<p className="section-eyebrow">Explain</p>
										<h3 id={id} className="text-lg font-bold tracking-tight">
											{title}
										</h3>
									</div>
									<button
										type="button"
										className="rounded-lg border border-border p-1.5 text-muted-foreground hover:text-foreground"
										aria-label="Close"
										onClick={() => setOpen(false)}
									>
										<X className="size-4" />
									</button>
								</div>
								<div className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
									{children}
								</div>
								<button
									type="button"
									className="btn-primary mt-5 w-full py-2.5 text-sm"
									onClick={() => setOpen(false)}
								>
									Got it
								</button>
							</div>
						</div>,
						document.body,
					)
				: null}
		</span>
	);
}
